import { createReducer } from 'typesafe-actions';
import { defaultErrorHandler, defaultRequestHandler } from '../dataFetching/generators';
import mapResponseToProduct from '../products/mapResponseToProduct';
import { ProductInterface } from '../products/types';
import { ProductLooksState } from './types';
import {
    fetchProductLooksActions,
    ProductLooksActionsType,
    resetLooksInfusionSliderViewAction,
    setLooksInfusionSliderViewAction,
} from './action';
import { TrackPDPLooksInfusionActions } from 'Common/ga/types';
import { LooksInfusionTrackLabel } from 'Client/components/ProductDetailPage/LooksInfusionSlider';

export const initialProductLooksState: ProductLooksState = {
    didInvalidate: false,
    identifier: '',
    isFetching: false,
    wasLooksInfusionViewed: false,
    looks: [],
};

export const productLooksReducer = createReducer<ProductLooksState, ProductLooksActionsType>(initialProductLooksState)
    .handleAction(setLooksInfusionSliderViewAction, state => ({
        ...state,
        wasLooksInfusionViewed: true,
    }))
    .handleAction(resetLooksInfusionSliderViewAction, state => ({
        ...state,
        wasLooksInfusionViewed: false,
    }))
    .handleAction(fetchProductLooksActions.request, defaultRequestHandler)
    .handleAction(fetchProductLooksActions.success, (state, action) => {
        const {
            data,
            identifier,
            reducerPayloadOptions: { sku, countryCode = '', currency = '', ga, launchDarklyTracker } = {},
        } = action.payload;

        const pdpSKU = sku;
        function getTrackingFunctions(
            sku: string,
            trackingName: string
        ): Pick<ProductInterface, 'clickTrackingFn' | 'wishListTrackingFn' | 'addToWishListExperimentFn'> {
            return {
                clickTrackingFn: () => {
                    return ga?.trackPDPInteractionEvent({
                        action: TrackPDPLooksInfusionActions.LookInfusionClick,
                        label: `PDP: ${pdpSKU}, Look: ${trackingName}, Product click ${sku}`,
                    });
                },
                wishListTrackingFn: ({ action, sku, name, brand, price, url, currency }) => {
                    if (ga) {
                        ga.trackWishListHeartClick({
                            action,
                            label: `PDP Look infusion,${sku}`,
                            sku,
                            name,
                            brand,
                            price,
                            url,
                            currency,
                        });
                    }
                },
                addToWishListExperimentFn: () => {
                    return (
                        ga?.trackExperiment({
                            action: TrackPDPLooksInfusionActions.LookInfusionABTestExperiment,
                            label: 'pdp-add-to-wishlist',
                        }),
                        launchDarklyTracker?.trackMetric(LooksInfusionTrackLabel.LAUNCH_DARKLY_ADD_TO_WISHLIST_METRIC)
                    );
                },
            };
        }
        const looks = data.looks.map(rawProductLook => {
            const products = rawProductLook.products.map((value, index) => {
                const product = mapResponseToProduct(index, value, undefined, undefined, { countryCode, currency });

                return {
                    ...product,
                    ...getTrackingFunctions(value.meta.sku, rawProductLook.trackingName),
                };
            });

            return {
                isLandscape: rawProductLook.isLandscape,
                trackingName: rawProductLook.trackingName,
                name: rawProductLook.name,
                slug: rawProductLook.slug,
                caption: rawProductLook.caption,
                color: rawProductLook.color,
                portraitListingImage: rawProductLook.portraitListingImage,
                moodImage: rawProductLook.moodImage,
                genericImagePortrait: rawProductLook.genericImagePortrait,
                products,
            };
        });
        return {
            ...state,
            identifier,
            isFetching: false,
            didInvalidate: false,
            reducerPayloadOptions: { sku },
            looks,
        };
    })
    .handleAction(fetchProductLooksActions.failure, (state, action) => defaultErrorHandler(state, action));
